import { useRef, useState } from 'react'
import { useOverflowFocus } from '@/hooks/use-overflow-focus'
import { AlertTriangle, CheckCircle2, Loader2, FileSpreadsheet } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useCommitBillingsLegacyImport, usePreviewBillingsLegacyImport } from '@/hooks/use-api'
import { explainError } from '@/lib/explain-error'
import type { BillingsLegacyPreview } from '@/types'

/**
 * Upload the Sales Tracker workbook.
 *
 * This brings the history across once. Jason works from the monthly QuickBooks export,
 * not from the workbook, which is why Upload.tsx folds this away behind a disclosure
 * rather than presenting it as an equal choice every month. An earlier version of this
 * comment called it a routine operation, which contradicted the page hosting it and was
 * wrong about how Jason actually works.
 *
 * Two things about re-running it are stated on screen, because both are easy to get
 * wrong silently:
 *
 *  - a rep correction he CHANGES in the workbook lands on the next upload
 *  - a reattribution somebody made on the Attribution screen is left alone
 *
 * The second is why the count of preserved decisions is shown after a commit
 * rather than being left implicit.
 */
export function LegacyWorkbookUpload() {
  const tabs = useOverflowFocus('What each tab of the workbook will contribute')
  const { toast } = useToast()
  const fileRef = useRef<HTMLInputElement>(null)

  const [preview, setPreview] = useState<BillingsLegacyPreview | null>(null)
  const [fxNa, setFxNa] = useState('1.0')
  const [fxAu, setFxAu] = useState('0.9')
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')

  const previewMutation = usePreviewBillingsLegacyImport()
  const commitMutation = useCommitBillingsLegacyImport()

  const money = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 0 })

  const handlePreview = async () => {
    const file = fileRef.current?.files?.[0]
    if (!file) {
      setError('Choose the Sales Tracker workbook first.')
      return
    }
    setError('')
    setPreview(null)
    setStatus('Reading the workbook. This one is large, so it can take a couple of minutes.')
    try {
      const result = await previewMutation.mutateAsync({ file, fxNa, fxAu })
      setPreview(result)
      setStatus(
        `${result.total_rows.toLocaleString()} rows and ${result.override_count.toLocaleString()} rep corrections read. Nothing has been saved yet.`
      )
    } catch (err) {
      setError(explainError(err, 'Could not read that workbook.'))
      setStatus('')
    }
  }

  const handleCommit = async () => {
    if (!preview) return
    try {
      const result = await commitMutation.mutateAsync(preview.preview_token)
      toast({
        title: 'Workbook imported',
        description:
          `${result.rows_written.toLocaleString()} lines and ` +
          `${result.overrides_written.toLocaleString()} rep corrections saved.` +
          (result.overrides_preserved_in_crm > 0
            ? ` ${result.overrides_preserved_in_crm} decision${result.overrides_preserved_in_crm === 1 ? '' : 's'} made in the CRM were kept.`
            : ''),
      })
      setPreview(null)
      setStatus('')
      if (fileRef.current) fileRef.current.value = ''
    } catch (err) {
      setError(explainError(err, 'The workbook could not be saved.'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Import history from the Sales Tracker workbook</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Reads both Data Input tabs and the Corrected Sales Rep column. Corrections you change in
          the workbook are picked up each time; anything reattributed here in the CRM is left alone.
        </p>

        <div className="grid gap-4 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Label htmlFor="legacy-file">Workbook</Label>
            <Input
              id="legacy-file"
              type="file"
              ref={fileRef}
              accept=".xlsm"
              aria-describedby="legacy-file-help"
              disabled={previewMutation.isPending}
            />
            <p id="legacy-file-help" className="text-xs text-muted-foreground mt-1">
              Sales_Tracker.xlsm itself, not a QuickBooks export.
            </p>
          </div>
          <div>
            <Label htmlFor="legacy-fx-na">Canada rate</Label>
            <Input
              id="legacy-fx-na"
              type="number"
              step="0.0001"
              min="0"
              value={fxNa}
              onChange={(e) => setFxNa(e.target.value)}
              disabled={previewMutation.isPending}
            />
          </div>
          <div>
            <Label htmlFor="legacy-fx-au">Australia rate</Label>
            <Input
              id="legacy-fx-au"
              type="number"
              step="0.0001"
              min="0"
              value={fxAu}
              onChange={(e) => setFxAu(e.target.value)}
              aria-describedby="legacy-fx-help"
              disabled={previewMutation.isPending}
            />
            <p id="legacy-fx-help" className="text-xs text-muted-foreground mt-1">
              Rates to CAD, pre-filled with the values the workbook used. This import usually runs
              against an empty CRM, so there is no established rate to fall back on and these
              values matter.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            onClick={handlePreview}
            disabled={previewMutation.isPending}
            aria-label="Read the workbook and show what will be imported"
          >
            {previewMutation.isPending ? (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
            ) : (
              <FileSpreadsheet className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {previewMutation.isPending ? 'Reading' : 'Read workbook'}
          </Button>
          <span className="text-sm text-muted-foreground">Nothing is saved at this step.</span>
        </div>

        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
          {status}
        </p>

        {error && (
          <Alert variant="destructive">
            <AlertDescription role="alert">{error}</AlertDescription>
          </Alert>
        )}

        {preview && (
          <div className="space-y-4 border-t pt-4">
            <div className="overflow-x-auto" ref={tabs.ref} {...tabs.regionProps}>
              <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                <caption className="sr-only">What each tab of the workbook will contribute</caption>
                <thead>
                  <tr className="border-b">
                    <th scope="col" className="text-left font-medium py-2 pr-3">
                      Tab
                    </th>
                    <th scope="col" className="text-right font-medium py-2 px-3">
                      Rows
                    </th>
                    <th scope="col" className="text-right font-medium py-2 px-3">
                      New
                    </th>
                    <th scope="col" className="text-right font-medium py-2 px-3">
                      Already present
                    </th>
                    <th scope="col" className="text-right font-medium py-2 px-3">
                      Corrections
                    </th>
                    <th scope="col" className="text-right font-medium py-2 pl-3">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {preview.tabs.map((t) => (
                    <tr key={t.sheet_name} className="border-b last:border-0">
                      <th scope="row" className="text-left font-normal py-1.5 pr-3">
                        {t.sheet_name}
                        {/* Stated because it is why AU rep attribution can never be
                            corrected in the spreadsheet. */}
                        {!t.has_correction_column && (
                          <span className="block text-xs text-muted-foreground">
                            no correction column
                          </span>
                        )}
                      </th>
                      <td className="text-right py-1.5 px-3">{t.total_rows.toLocaleString()}</td>
                      <td className="text-right py-1.5 px-3">{t.new_rows.toLocaleString()}</td>
                      <td className="text-right py-1.5 px-3">
                        {t.refreshed_rows.toLocaleString()}
                      </td>
                      <td className="text-right py-1.5 px-3">
                        {t.corrections_in_tab.toLocaleString()}
                      </td>
                      <td className="text-right py-1.5 pl-3">{money(t.normalised_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Alert role="note">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>
                {preview.override_count.toLocaleString()} rep corrections will be applied
              </AlertTitle>
              <AlertDescription>
                From {preview.correction_rows.toLocaleString()} corrected rows. Identical lines
                share one decision.
                {preview.overrides_canonicalised > 0 && (
                  <>
                    {' '}
                    {preview.overrides_canonicalised.toLocaleString()} were tidied onto standard
                    names, so one rep does not appear twice under different spellings.
                  </>
                )}
                {preview.override_summary.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {preview.override_summary.slice(0, 6).map((o) => (
                      <li key={o.label}>
                        <span className="font-medium">{o.label}</span> — {o.count.toLocaleString()}
                      </li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>

            {preview.conflicts.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  {preview.conflicts.length} conflicting correction
                  {preview.conflicts.length === 1 ? '' : 's'}
                </AlertTitle>
                <AlertDescription>
                  Identical lines were corrected two different ways in the workbook. The first is
                  kept; check these afterwards.
                </AlertDescription>
              </Alert>
            )}

            {preview.tabs.some((t) => t.unmapped_reps.length > 0) && (
              <Alert role="note">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>Some rep names are not recognised</AlertTitle>
                <AlertDescription>
                  Those lines will count in the totals but be attributed to nobody until mapped on
                  the Attribution screen.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center gap-3">
              <Button
                type="button"
                onClick={handleCommit}
                disabled={commitMutation.isPending}
                aria-label="Save the workbook import"
              >
                {commitMutation.isPending && (
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
                {commitMutation.isPending ? 'Saving' : 'Save workbook'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPreview(null)
                  setStatus('')
                }}
                disabled={commitMutation.isPending}
              >
                Discard
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
